import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { fetchCurrencyComparison } from '../../../api';
import { useChild } from '../../../context/ChildContext';
import { UserContext } from '../../../context/UserContext';
import './CurrencyComparison.css';

const EUTooltip = () => (
  <div className="tooltip eu-tooltip">
    <h3>🇪🇺 The European Union Adventure! 🇪🇺</h3>
    <p>Wow! You've discovered something special - the European Union (EU)!</p>
    <p>The EU is like a big team of countries working together. They share the same money called the Euro (€).</p>
    <p><strong>Fun Facts:</strong></p>
    <ul>
      <li>The EU has 27 countries!</li>
      <li>They work together on things like trade, travel, and protecting the environment.</li>
      <li>The EU flag has 12 gold stars on a blue background. The stars stand for unity!</li>
    </ul>
    <p>Can you name some EU countries? Here are a few: France, Germany, Italy, Spain...</p>
    <p><a href="https://europa.eu/learning-corner/home_en" target="_blank" rel="noopener noreferrer">Learn more about the EU!</a></p>
  </div>
);

const CountryTooltip = ({ country }) => {
  const [countryDetails, setCountryDetails] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCountryDetails = async () => {
      if (country === 'European Union') {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`https://restcountries.com/v3.1/name/${country}?fullText=true`);
        if (!response.ok) throw new Error('Failed to fetch country details');
        const data = await response.json();
        setCountryDetails(data[0]);
      } catch (err) {
        setError('Failed to load country details');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCountryDetails();
  }, [country]);

  if (country === 'European Union') return <EUTooltip />;
  if (isLoading) return <div className="tooltip">Loading country details...</div>;
  if (error) return <div className="tooltip">{error}</div>;
  if (!countryDetails) return null;

  return (
    <div className="tooltip">
      <p><strong>Region:</strong> {countryDetails.region}</p>
      <p><strong>Subregion:</strong> {countryDetails.subregion}</p>
      <p><strong>Languages:</strong> {Object.values(countryDetails.languages).join(', ')}</p>
      <p><strong>Capital:</strong> {countryDetails.capital[0]}</p>
      <p><strong>Population:</strong> {countryDetails.population.toLocaleString()}</p>
      <p><strong>Map:</strong> <a href={countryDetails.maps.openStreetMaps} target="_blank" rel="noopener noreferrer">View on OpenStreetMap</a></p>
    </div>
  );
};

const CurrencyCard = ({ comparison, childBalance, onMouseEnter, onMouseLeave, isHovered }) => {
  const formatCurrency = (amount, code) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div 
      className="currency-card"
      onMouseEnter={() => onMouseEnter(comparison.country)}
      onMouseLeave={onMouseLeave}
    >
      <div className="card-header">
        <span className="country-flag">{comparison.flag}</span>
        <span className="country-name">{comparison.country}</span>
      </div>
      <div className="card-middle">
        <span className="currency-name">{comparison.currencyName}</span>
        <span className="exchange-rate-original">Rate: {comparison.exchangeRate}</span>
      </div>
      <div className="card-body">
        <span className="exchange-rate">
          {formatCurrency(parseFloat(comparison.exchangeRate) * childBalance, comparison.currencyCode)}
        </span>
      </div>
      {isHovered && <CountryTooltip country={comparison.country} />}
    </div>
  );
};

const CurrencyComparison = () => {
  const [comparisonData, setComparisonData] = useState(null);
  const [error, setError] = useState(null);
  const { dashboardData, currencyCode } = useChild();
  const { user } = useContext(UserContext);
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (user.details && user.details.family_id) {
        try {
          const data = await fetchCurrencyComparison(user.details.family_id);
          if (isMounted) {
            setComparisonData(data);
            setError(null); // Clear any previous errors
          }
        } catch (err) {
          if (isMounted) {
            setError('Failed to fetch currency comparison data');
            console.error('Currency comparison fetch error:', err);
          }
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [user.details]);

  const handleMouseEnter = useCallback((country) => {
    clearTimeout(timeoutRef.current);
    setHoveredCountry(country);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setHoveredCountry(null);
    }, 500); // 500ms delay before hiding the tooltip
  }, []);

  const formatCurrency = useCallback((amount, code) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }, []);

  if (error) return <div className="error-message">{error}</div>;
  if (!comparisonData || !dashboardData) return <div className="loading-message">Loading currency comparison...</div>;

  const childBalance = dashboardData.totalAccountsBalance || 0;

  return (
    <div className="currency-comparison-container">
      <div className="currency-comparison-text">
        <h2>Your Money's World Tour!</h2>
        <p>Guess what? Your money can be a world traveler, just like you!</p>
        <p>Right now, your piggy bank is holding: <strong>{formatCurrency(childBalance, currencyCode)}</strong> 🐷💰</p>
        <p>But what if your money decided to go on an adventure? Let's find out how much it would be worth in different countries!</p>
      </div>
      <div className="currency-cards-container">
        {comparisonData.comparisons.map((comparison) => (
          <CurrencyCard
            key={comparison.currencyCode}
            comparison={comparison}
            childBalance={childBalance}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            isHovered={hoveredCountry === comparison.country}
          />
        ))}
      </div>
      <div className="currency-comparison-footer">
        <p>Ready for a fun fact? In some places, your money might make you feel like a millionaire, while in others, it might only buy you a few candies! 🍭</p>
        <p>Isn't it amazing how your money can speak so many different languages? 💬</p>
        <p>Remember, no matter where you go, saving is always a smart move! Keep growing your piggy bank, and who knows? Maybe one day you'll visit some of these places and see for yourself! ✈️</p>
        <h6>Exchange rates updated : {comparisonData.latestDate}</h6>
      </div>
    </div>
  );
};

export default CurrencyComparison;